import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  ClientInsurance,
  ClientInsuranceCreateAttributes,
  IClientInsuranceResponse,
} from '@sondermind/utilities/models-client-insurance';
import {
  IAPIFetchRequestOptions,
  IAPIFetchResponse,
  IAPIListRequestOptions,
  IAPIListResponse,
} from '@sondermind/utilities/models-http';

import {
  IBenefitsInformation,
  IPatientInsurance,
  convertLastBenefitInformation,
  convertPatientInsurance,
} from '../models/patient-insurance.interface';

export type CashPayId = { persona_client_id: number; } | { patient_id: number; };

@Injectable({
  providedIn: 'root',
})
export class ClientInsuranceHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) { }

  list$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IPatientInsurance[]> {
    const sanitized = this.sanitizePersonaClientIdFilter(opts);
    if (!sanitized) {
      return of([]);
    }
    const params = this.http.generateListParams(sanitized);

    return this.http.get<IAPIListResponse<IPatientInsurance>>(this.indexUrl, params)
      .pipe(
        map((resp) => resp.data.map((ins) => convertPatientInsurance(ins))),
        this.http.handleHttpError(opts.error)
      );
  }

  /**
   * Same request as `list$`, but keeps the response envelope instead of unwrapping to `data`.
   * Use this for paginated tables: `ListResource` needs `meta.counts` and `meta.links` to render
   * page controls. Prefer `list$` when you only need the records. Records are converted either way,
   * so "raw" here means the full envelope, not unconverted data.
   */
  listRaw$(
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IAPIListResponse<IPatientInsurance>> {
    const sanitized = this.sanitizePersonaClientIdFilter(opts);
    if (!sanitized) {
      return of({ data: [] } as IAPIListResponse<IPatientInsurance>);
    }
    const params = this.http.generateListParams(sanitized);

    return this.http.get<IAPIListResponse<any>>(this.indexUrl, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((ins) => convertPatientInsurance(ins)),
        })),
        this.http.handleHttpError(opts.error)
      );
  }

  fetch$(
    id: number,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IPatientInsurance> {
    const params = this.http.generateListParams(opts);

    return this.http.get<IAPIFetchResponse<any>>(this.fetchUrl(id), params)
      .pipe(
        map((res) => convertPatientInsurance(res.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  create(
    body: ClientInsuranceCreateAttributes,
    accountSetupContext: string = null,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientInsurance> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<IAPIFetchResponse<IClientInsuranceResponse>>(
      this.indexUrl,
      { patient_insurance: body, account_setup_context: accountSetupContext },
      params
    )
      .pipe(
        map((resp) => new ClientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  update(
    id: number | string,
    body: ClientInsuranceCreateAttributes,
    accountSetupContext: string = null,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientInsurance> {
    const params = this.http.generateFetchParams(opts);

    return this.http.patch<IAPIFetchResponse<IClientInsuranceResponse>>(
      this.fetchUrl(id),
      { patient_insurance: body, account_setup_context: accountSetupContext },
      params
    ).pipe(
      map((resp) => new ClientInsurance(resp.data)),
      this.http.handleHttpError(opts.error)
    );
  }

  addCashPay(
    cashPayId?: CashPayId,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IPatientInsurance[]> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<IAPIListResponse<IPatientInsurance>>(
      `${this.indexUrl}/add_cash_pay`,
      cashPayId,
      params
    )
      .pipe(
        map((resp) => resp.data.map((ins) => convertPatientInsurance(ins))),
        this.http.handleHttpError(opts.error)
      );
  }

  addProBono(
    patient_id: number,
    add_redemptions: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IPatientInsurance> {
    const params = this.http.generateServiceParams(opts);

    return this.http.post<IAPIFetchResponse<IPatientInsurance>>(
      `${this.indexUrl}/add_pro_bono`,
      { patient_id, add_redemptions },
      params
    )
      .pipe(
        map((resp) => convertPatientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  delete(
    id: number | string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<null> {
    const params = this.http.generateFetchParams(opts);

    return this.http.delete<null>(this.fetchUrl(id), params)
      .pipe(this.http.handleHttpError(opts.error));
  }

  deleteMultiple(
    ids: number[],
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<null> {
    const params = this.http.generateFetchParams(opts);

    return this.http.post<null>(`${this.indexUrl}/bulk_archive`, { patient_insurance_ids: ids }, params)
      .pipe(this.http.handleHttpError(opts.error));
  }

  ignoreFailedBenefitsVerification(
    idOrInsurance: number | IPatientInsurance,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IPatientInsurance> {
    const params = this.http.generateFetchParams(opts);
    const id = typeof idOrInsurance === 'number' ? idOrInsurance : idOrInsurance.id;

    return this.http.post<IAPIFetchResponse<IPatientInsurance>>(
      `${this.fetchUrl(id)}/ignore_bv_failure`,
      {},
      params
    )
      .pipe(
        map((resp) => convertPatientInsurance(resp.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  fetchLastBenefitInformation$(
    id: number,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IBenefitsInformation> {
    if (id == null || id === undefined) {
      return of(null);
    }

    // Uncomment line below to test local json files and comment all code below it inside this method
    // return of(convertLastBenefitInformation(benefitsData.data));

    const params = this.http.generateListParams(opts);

    return this.http.get<IAPIFetchResponse<any>>(`${this.fetchUrl(id)}/last_benefit_information`, params)
      .pipe(
        map((res) => convertLastBenefitInformation(res.data)),
        this.http.handleHttpError(opts.error)
      );
  }

  get indexUrl(): string {
    return `${this.configurationService.env.svcBase}/billing/patient_insurances`;
  }

  fetchUrl(id: number | string): string {
    return `${this.indexUrl}/${id}`;
  }

  // Rails types filter[persona_client_id] as a strict Integer. A nullish or "null"
  // value causes SoberSwag to raise and the endpoint 500s. Drop the request so
  // callers with stale state (or unguarded call sites) can't page on-call.
  private sanitizePersonaClientIdFilter(
    opts: Partial<IAPIListRequestOptions>
  ): Partial<IAPIListRequestOptions> | null {
    if (!opts.filter) {
      return opts;
    }

    const filters = Array.isArray(opts.filter) ? opts.filter : [opts.filter];
    const hasInvalid = filters.some((f) => f.key === 'persona_client_id' && this.isInvalidPersonaClientIdValue(f.value));

    if (!hasInvalid) {
      return opts;
    }

    return null;
  }

  private isInvalidPersonaClientIdValue(value: unknown): boolean {
    return value == null || value === '' || value === 'null' || value === 'undefined';
  }
}
